import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../contexts/UserContext";
import {
  clearStoredActiveSessionId,
  getCurrentAuthToken,
  getStoredActiveSessionId,
  logSessionEvent,
} from "./loginEvents";
import { logToConsole } from "./utilities";

const INACTIVITY_LIMIT_MS = 10 * 60 * 1000; // 10 minutes
const WARNING_THRESHOLD_MS = 1 * 60 * 1000; // show banner 1 minute before logout
const WARNING_SECONDS = Math.ceil(WARNING_THRESHOLD_MS / 1000);
const LAST_ACTIVITY_KEY = "lastActivity";

export function useAutoLogout() {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [showWarning, setShowWarning] = useState(false);
  const [warningTimeLeft, setWarningTimeLeft] = useState(WARNING_SECONDS);
  const { signOut, isAuthenticated, user } = useUser();

  const warningTimeoutRef = useRef<number | null>(null);
  const isLoggingOutRef = useRef(false);

  const clearWarningTimeout = useCallback(() => {
    if (warningTimeoutRef.current !== null) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  // ── forceLogout ──────────────────────────────────────────────────────────
  const forceLogout = useCallback(
    async (reasonMessage: string) => {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;

      clearWarningTimeout();
      setShowWarning(false);
      localStorage.removeItem(LAST_ACTIVITY_KEY);

      const userId = user?.userId as string | undefined;
      const sessionId = getStoredActiveSessionId();
      const authToken = await getCurrentAuthToken();

      try {
        await signOut();

        if (userId && sessionId) {
          await logSessionEvent({
            eventType: "LOGOUT_TIMEOUT",
            userId,
            sessionId,
            authToken,
          });
        }

        clearStoredActiveSessionId();
        navigate("/", {
          replace: true,
          state: { toast: { type: "info", message: reasonMessage } },
        });
      } catch (err) {
        logToConsole("error", "Auto logout failed:", err);
      } finally {
        window.setTimeout(() => {
          isLoggingOutRef.current = false;
        }, 0);
      }
    },
    [clearWarningTimeout, navigate, signOut, user?.userId],
  );

  // Stable ref so the countdown interval always calls the latest forceLogout
  const forceLogoutRef = useRef(forceLogout);
  useEffect(() => {
    forceLogoutRef.current = forceLogout;
  }, [forceLogout]);

  // ── scheduleWarning ──────────────────────────────────────────────────────
  // Schedules the warning banner to appear `delayMs` from now.
  const scheduleWarning = useCallback(
    (delayMs: number) => {
      clearWarningTimeout();
      warningTimeoutRef.current = window.setTimeout(() => {
        setShowWarning(true);
        setWarningTimeLeft(WARNING_SECONDS);
      }, delayMs);
    },
    [clearWarningTimeout],
  );

  // ── resetTimers ──────────────────────────────────────────────────────────
  // Called on login and on every qualifying activity event.
  const resetTimers = useCallback(() => {
    if (!isAuthenticated) return;
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    setShowWarning(false);
    scheduleWarning(INACTIVITY_LIMIT_MS - WARNING_THRESHOLD_MS);
  }, [isAuthenticated, scheduleWarning]);

  // Stable ref so the event listener never goes stale
  const resetTimersRef = useRef(resetTimers);
  useEffect(() => {
    resetTimersRef.current = resetTimers;
  }, [resetTimers]);

  // ── Countdown interval ───────────────────────────────────────────────────
  // Owns the actual logout — no separate setTimeout race possible.
  useEffect(() => {
    if (!showWarning) return;

    setWarningTimeLeft(WARNING_SECONDS);

    const intervalId = window.setInterval(() => {
      setWarningTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          window.clearInterval(intervalId);
          void forceLogoutRef.current(t("errorMessage.loggedOut"));
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWarning]);

  // ── Stable activity handler ──────────────────────────────────────────────
  const handleActivity = useRef(() => {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    const now = Date.now();
    // If the user was away past the inactivity limit, force logout immediately
    if (lastActivity && now - Number(lastActivity) >= INACTIVITY_LIMIT_MS) {
      void forceLogoutRef.current(t("errorMessage.loggedOut"));
      return;
    }
    // Throttle: only reset if 5 s have passed since last recorded activity
    if (!lastActivity || now - Number(lastActivity) > 5000) {
      resetTimersRef.current();
    }
  }).current;

  const stayLoggedIn = useCallback(() => {
    setShowWarning(false);
    resetTimers();
  }, [resetTimers]);

  // ── Auth effect ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated) {
      setShowWarning(false);
      setWarningTimeLeft(WARNING_SECONDS);
      clearWarningTimeout();
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      return;
    }

    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);

    if (!lastActivity) {
      // Fresh login — start the clock now
      resetTimers();
    } else {
      // Returning session (e.g. page refresh, tab reopen)
      const elapsed = Date.now() - Number(lastActivity);

      if (elapsed >= INACTIVITY_LIMIT_MS) {
        // Already past the limit — force logout immediately
        void forceLogoutRef.current(t("errorMessage.loggedOut"));
        return;
      }

      if (elapsed >= INACTIVITY_LIMIT_MS - WARNING_THRESHOLD_MS) {
        // Inside the warning window — show banner with correct remaining seconds
        const remaining = Math.ceil((INACTIVITY_LIMIT_MS - elapsed) / 1000);
        setWarningTimeLeft(remaining);
        setShowWarning(true);
      } else {
        // Still safe — resume the warning timeout from where we left off
        scheduleWarning(INACTIVITY_LIMIT_MS - WARNING_THRESHOLD_MS - elapsed);
      }
    }

    const activityEvents: Array<keyof WindowEventMap> = [
      "keydown",
      "pointerdown",
      "scroll",
    ];
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
        if (
          lastActivity &&
          Date.now() - Number(lastActivity) >= INACTIVITY_LIMIT_MS
        ) {
          void forceLogoutRef.current(t("errorMessage.loggedOut"));
        }
      }
    };

    activityEvents.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true }),
    );
    window.addEventListener("visibilitychange", onVisibility);

    return () => {
      activityEvents.forEach((e) =>
        window.removeEventListener(e, handleActivity),
      );
      window.removeEventListener("visibilitychange", onVisibility);
      clearWarningTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return { showWarning, stayLoggedIn, warningTimeLeft };
}
